<div class="page-container">
  <div class="legal-content scrollable-content">
    <h1>{{ 'legal.privacy.title' | translate }}</h1>
    <p class="updated">{{ 'legal.privacy.lastUpdated' | translate }}</p>

    <p>
      This Privacy Policy explains how Garden Bay Software ("Garden Bay Software", "we", "us", or "our") collects,
      uses, and protects information in connection with CheckPoint (the "Service"). It applies to the users of
      organizations that use the Service.
    </p>

    <h2>1. Information We Collect</h2>
    <p><strong>Account information.</strong> When you sign up or are invited to an organization, we collect your
      name, email address, and organization membership/role.</p>
    <p><strong>Your app data.</strong> CheckPoint is offline-first: the assets, checkpoints, forms, submissions, and
      reports you create are stored locally on your device first, and synced to servers we operate so that other
      authorized members of your organization can see them and so the data is backed up. We treat this content as
      belonging to your organization, not as our own.</p>
    <p><strong>Billing information.</strong> If your organization has a paid subscription, our payment processor,
      Stripe, collects your payment card details directly. We receive and store limited billing metadata from
      Stripe — such as subscription plan, status, invoice history, and card brand/last 4 digits — but never your full
      card number.</p>
    <p><strong>Technical and diagnostic data.</strong> We collect basic technical information (such as browser type,
      device information, and error logs) to operate, secure, and troubleshoot the Service. We don't use advertising
      trackers or third-party analytics cookies.</p>

    <h2>2. How We Use Information</h2>
    <ul>
      <li>To provide, maintain, and sync the Service for your organization;</li>
      <li>To authenticate you and enforce access permissions within your organization;</li>
      <li>To process payments and manage subscriptions;</li>
      <li>To communicate with you about your account, updates, or support requests;</li>
      <li>To diagnose problems, secure the Service, and prevent misuse.</li>
    </ul>

    <h2>3. Local-First Storage and Sync</h2>
    <p>
      Because CheckPoint works offline, most of your organization's data lives on each user's device in a local
      database, and stays usable even without a network connection. Our servers act as a sync and coordination point
      so that data entered on one device becomes visible to authorized teammates on other devices, and so it's backed
      up outside any single device. We don't process this data for any purpose beyond providing the Service.
    </p>

    <h2>4. How We Share Information</h2>
    <p>
      We don't sell your information. We share information only with:
    </p>
    <ul>
      <li>
        <strong>Stripe</strong>, to process payments and manage subscriptions (see
        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy
          Policy</a>);
      </li>
      <li>Other members of your organization, as intended by the Service's permission model;</li>
      <li>Third parties, if required to comply with a legal obligation, or to protect the rights, safety, or
        property of Garden Bay Software, our users, or others.</li>
    </ul>

    <h2>5. Data Retention</h2>
    <p>
      We retain your organization's data for as long as your account is active, and for a reasonable period
      afterward in case you wish to reactivate or export it, or as needed to comply with legal obligations. You can
      request deletion of your account or organization's data as described in Section 7 below.
    </p>

    <h2>6. Data Security</h2>
    <p>
      We use reasonable technical and organizational measures to protect your information, including encrypted
      connections between your device and our servers. No method of transmission or storage is completely secure,
      and we can't guarantee absolute security.
    </p>

    <h2>7. Your Rights and Choices</h2>
    <p>
      Depending on where you live, you may have rights to access, correct, export, or request deletion of your
      personal information. To make a request, contact us at
      <a href="mailto:support@gardenbaysoftware.com">support&#64;gardenbaysoftware.com</a>. If you're a member of an
      organization rather than its owner/admin, some requests may need to be made through your organization, since
      the organization controls its own data within the Service.
    </p>

    <h2>8. Children's Privacy</h2>
    <p>
      The Service is intended for business and professional use and is not directed to children. We don't knowingly
      collect personal information from children under 16.
    </p>

    <h2>9. International Data Transfers</h2>
    <p>
      Your information may be processed in Canada or other countries where we or our service providers (such as
      Stripe) operate, which may have different data protection laws than your own country.
    </p>

    <h2>10. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. If we make material changes, we'll notify you (for
      example, by email or through the Service) before they take effect.
    </p>

    <h2>11. Contact</h2>
    <p>
      Questions about this Privacy Policy? Contact us at
      <a href="mailto:support@gardenbaysoftware.com">support&#64;gardenbaysoftware.com</a>.
    </p>

    <p class="updated">
      This document was adapted from the
      <a href="https://github.com/Automattic/legalmattic" target="_blank" rel="noopener noreferrer">Automattic
        Legalmattic</a> template, licensed under
      <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer"
        >CC BY-SA 4.0</a
      >.
    </p>

    <a routerLink="/landing" class="back-link" data-testid="privacy-back-link">{{
      'legal.backToHome' | translate
    }}</a>
  </div>
</div>
